import React from 'react';
import { Link } from 'react-router-dom';
import {
  Swords, ChevronLeft, Clock, Shield, Users, Zap,
  Flame, Wind, AlertTriangle, Heart,
  MessageSquare, ChevronRight, ArrowRight, Gem,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const STEPS = [
  {
    num: '01',
    title: 'Maestro Mítico',
    desc: 'Visita al Maestro Mítico en las ciudades principales. Compra tu Piedra y revisa los afijos del nivel que quieres afrontar.',
  },
  {
    num: '02',
    title: 'Elige tu nivel',
    desc: 'De M+1 a M+7. Cada nivel suma afijos que hacen la mazmorra más letal. Empieza bajo, escala cuando estés listo.',
  },
  {
    num: '03',
    title: 'Activa la Piedra',
    desc: 'El líder usa la Piedra al entrar en una mazmorra compatible. 10 segundos de cuenta atrás y el desafío comienza.',
  },
  {
    num: '04',
    title: 'Vence el cronómetro',
    desc: 'Elimina al jefe final antes de que expire el tiempo. Todo el grupo recibe recompensas y una nueva Piedra automáticamente.',
  },
];

const LEVELS = [
  { num: 'M+1', timer: '45 min', gear: 'iLvl 232',             dots: 1, color: '#34D399' },
  { num: 'M+2', timer: '45 min', gear: 'iLvl 232',             dots: 1, color: '#A3E635' },
  { num: 'M+3', timer: '40 min', gear: 'iLvl 232',             dots: 2, color: '#FCD34D' },
  { num: 'M+4', timer: '40 min', gear: 'iLvl 245',             dots: 2, color: '#FB923C' },
  { num: 'M+5', timer: '40 min', gear: 'iLvl 245',             dots: 3, color: '#F97316' },
  { num: 'M+6', timer: '40 min', gear: 'iLvl 245 / prob. 258', dots: 3, color: '#EF4444' },
  { num: 'M+7', timer: '40 min', gear: 'iLvl 245 / prob. 258', dots: 4, color: '#A855F7' },
];

const TOP_HEIGHTS = [2, 3, 4, 6, 8, 10, 13];

const AFFIXES = [
  {
    icon: Heart,
    name: 'Vida Reforzada',
    desc: 'Todos los enemigos tienen más puntos de vida. Los combates se alargan y la curación cobra mayor importancia.',
    color: 'rgba(239,68,68,0.10)',
    border: 'rgba(239,68,68,0.28)',
  },
  {
    icon: Shield,
    name: 'Voluntad de Hierro',
    desc: 'Los jefes tienen vida adicional. Prepara a tu grupo para enfrentamientos más largos y exigentes.',
    color: 'rgba(239,68,68,0.08)',
    border: 'rgba(239,68,68,0.20)',
  },
  {
    icon: Users,
    name: 'Horda Infinita',
    desc: 'El trash puede invocar copias de sí mismo al iniciar combate. El control de masas es fundamental.',
    color: 'rgba(251,146,60,0.10)',
    border: 'rgba(251,146,60,0.28)',
  },
  {
    icon: Swords,
    name: 'Furia Oscura',
    desc: 'Los enemigos infligen más daño. Un momento de descuido puede ser fatal para cualquier miembro del grupo.',
    color: 'rgba(239,68,68,0.12)',
    border: 'rgba(239,68,68,0.35)',
  },
  {
    icon: Flame,
    name: 'Explosión Caótica',
    desc: 'Los enemigos pueden explotar aleatoriamente. Mantén al grupo disperso y a tu sanador alerta.',
    color: 'rgba(251,146,60,0.13)',
    border: 'rgba(251,146,60,0.35)',
  },
  {
    icon: Zap,
    name: 'Esferas de Relámpago',
    desc: 'Esferas eléctricas aparecen periódicamente en la mazmorra. Esquívalas antes de que te alcancen.',
    color: 'rgba(0,229,255,0.10)',
    border: 'rgba(0,229,255,0.28)',
  },
  {
    icon: AlertTriangle,
    name: 'Frenesí',
    desc: 'Cualquier enemigo puede enfurecerse sin previo aviso. Los interruptores y el CC son clave para sobrevivir.',
    color: 'rgba(251,146,60,0.10)',
    border: 'rgba(251,146,60,0.25)',
  },
  {
    icon: Wind,
    name: 'Raíces Arcanas',
    desc: 'Los enemigos pueden inmovilizar a los jugadores con raíces mágicas. La movilidad del grupo es crucial.',
    color: 'rgba(52,211,153,0.10)',
    border: 'rgba(52,211,153,0.28)',
  },
];

const DUNGEONS = [
  { name: 'Foso de Saron' },
  { name: 'La Forja de Almas' },
  { name: "Ahn'kahet: El Antiguo Reino" },
  { name: 'Azjol-Nerub' },
  { name: "Fortaleza de Drak'Tharon" },
  { name: 'Gundrak' },
  { name: 'Salas de Piedra' },
  { name: 'Pináculo de Utgarde' },
  { name: 'El Nexo' },
  { name: 'El Oculus' },
  { name: 'Fortaleza de Utgarde' },
  { name: 'El Bastión Violeta' },
  { name: 'Sima Ígnea', scaled: true },
  { name: 'Zahúrda Rajacieno', scaled: true },
];

function LevelProgressBar() {
  return (
    <div className="mp-progress-wrapper">
      {LEVELS.map((lvl, idx) => (
        <div key={lvl.num} className="mp-progress-seg">
          <div className="mp-progress-top" style={{ background: lvl.color, height: TOP_HEIGHTS[idx] }} />
          <div className="mp-progress-body">
            <div className="mp-progress-badge" style={{ color: lvl.color }}>{lvl.num}</div>
            <div className="mp-progress-meta">
              <Clock size={9} />
              {lvl.timer}
            </div>
            {lvl.gear !== '—' && <div className="mp-progress-gear">{lvl.gear}</div>}
            <div className="mp-progress-dots">
              {Array.from({ length: lvl.dots }).map((_, i) => (
                <span key={i} className="mp-progress-dot" style={{ background: lvl.color }} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AffixCard({ item }) {
  const Icon = item.icon;
  const iconColor = item.border
    .replace('0.28', '0.9').replace('0.25', '0.9').replace('0.35', '0.9')
    .replace('0.20', '0.9').replace('0.30', '0.9');
  return (
    <div className="mp-affix-card" style={{ background: item.color, borderColor: item.border }}>
      <div className="mp-affix-header">
        <div className="mp-affix-icon" style={{ color: iconColor }}><Icon size={16} /></div>
        <span className="mp-affix-name">{item.name}</span>
      </div>
      <p className="mp-affix-desc">{item.desc}</p>
    </div>
  );
}

export default function MythicPlusPage() {
  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Mythic+ | Frostbound WotLK"
        description="Desafía los límites en Mythic+. 7 niveles de dificultad con afijos, cronómetro y recompensas únicas en Frostbound."
        canonical="/jugabilidad/mythic-plus"
      />
      <Navbar />

      <main className="mp-main">

        {/* HERO */}
        <section className="mp-hero">
          <div className="mp-hero-bg" style={{ backgroundImage: "url('/sistemas/mythic-plus.jpg')" }} />
          <div className="mp-hero-overlay" />
          <div className="mp-hero-content anim-fade-up">
            <Link to="/jugabilidad" className="mp-back-link">
              <ChevronLeft size={14} />
              JUGABILIDAD
            </Link>
            <div className="mp-hero-icon"><Swords size={36} /></div>
            <h1 className="mp-hero-title">MYTHIC+</h1>
            <p className="mp-hero-subtitle">
              El desafío definitivo de mazmorra.{' '}
              <span className="cyan-highlight">Siete niveles</span> de dificultad escalable,
              afijos que redefinen el combate y recompensas que solo los mejores merecen.
            </p>
          </div>
        </section>

        {/* INTRO */}
        <section className="mp-intro anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <p className="mp-intro-text">
            Mythic+ convierte las mazmorras en desafíos escalables con cronómetro, afijos y recompensas crecientes.
            Cada nivel aumenta la dificultad y exige mayor coordinación para obtener mejores recompensas.
          </p>
        </section>

        {/* CÓMO FUNCIONA */}
        <section className="mp-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
          <div className="mp-section-header">
            <h2 className="mp-section-title">Cómo funciona</h2>
          </div>
          <div className="mp-steps">
            {STEPS.map((step) => (
              <div key={step.num} className="mp-step">
                <div className="mp-step-num">{step.num}</div>
                <h3 className="mp-step-title">{step.title}</h3>
                <p className="mp-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* LOS 7 NIVELES */}
        <section className="mp-section anim-fade-up" style={{ animationDelay: '0.2s' }}>
          <div className="mp-section-header">
            <h2 className="mp-section-title">Los 7 niveles</h2>
            <p className="mp-section-sub">
              El equipo es intercambiable hasta 2h después de completar la mazmorra y se filtra por armadura de clase.
              En{' '}<span style={{ color: '#EF4444', fontWeight: 700 }}>M+6</span>{' '}y{' '}
              <span style={{ color: '#A855F7', fontWeight: 700 }}>M+7</span>{' '}
              existe probabilidad adicional de obtener piezas iLvl 258.
            </p>
          </div>
          <LevelProgressBar />
        </section>

        {/* AFIJOS */}
        <section className="mp-section anim-fade-up" style={{ animationDelay: '0.25s' }}>
          <div className="mp-section-header">
            <h2 className="mp-section-title">Afijos</h2>
            <p className="mp-section-sub">
              Cada nivel activa una combinación de afijos. Conocerlos antes de entrar marca la diferencia
              entre completar en tiempo y salir con las manos vacías.
            </p>
          </div>
          <div className="mp-affixes-grid">
            {AFFIXES.map((affix) => (
              <AffixCard key={affix.name} item={affix} />
            ))}
          </div>
        </section>

        {/* MAZMORRAS */}
        <section className="mp-section anim-fade-up" style={{ animationDelay: '0.3s' }}>
          <div className="mp-section-header">
            <h2 className="mp-section-title">Mazmorras disponibles</h2>
            <p className="mp-section-sub">
              14 mazmorras compatibles. Las marcadas con{' '}
              <span className="mp-scaled-tag">ESCALADA</span>{' '}
              adaptan su dificultad automáticamente al nivel 80.
            </p>
          </div>
          <div className="mp-dungeon-grid">
            {DUNGEONS.map((d) => (
              <div key={d.name} className="mp-dungeon-item">
                <ChevronRight size={10} className="mp-dungeon-chevron" />
                {d.name}
                {d.scaled && <span className="mp-scaled-badge">ESCALADA</span>}
              </div>
            ))}
          </div>
        </section>

        {/* LA PIEDRA MÍTICA + COMANDO */}
        <section className="mp-section anim-fade-up" style={{ animationDelay: '0.35s' }}>
          <div className="mp-section-header">
            <h2 className="mp-section-title">La Piedra Mítica</h2>
          </div>
          <div className="mp-keystone-box">
            <div className="mp-keystone-icon"><Gem size={22} /></div>
            <p className="mp-keystone-text">
              La <strong>Piedra de Mazmorra Mítica</strong> se adquiere del Maestro Mítico y tiene un
              tiempo de reutilización de <strong>2 horas</strong>. Si completas la mazmorra dentro del
              tiempo límite, <strong>cada miembro del grupo recibe automáticamente una nueva piedra</strong>.
              Solo el líder puede activarla para iniciar el desafío.
            </p>
          </div>
          <div className="mp-command-row">
            <code className="mp-cmd">.mythic info</code>
            <span className="mp-cmd-desc">Ver información del Mythic+ activo en la mazmorra actual.</span>
          </div>
          <p className="mp-commands-note">
            <MessageSquare size={13} style={{ marginRight: 5, flexShrink: 0 }} />
            Solo funciona mientras estás dentro de una mazmorra Mythic Plus activa.
          </p>
        </section>

        {/* CTA */}
        <div className="mp-cta anim-fade-up" style={{ animationDelay: '0.4s' }}>
          <p className="mp-cta-text">DESCUBRE TODOS LOS SISTEMAS DE FROSTBOUND.</p>
          <Link to="/jugabilidad" className="cta-button" style={{ gap: 8 }}>
            JUGABILIDAD <ArrowRight size={14} />
          </Link>
        </div>

      </main>

      <Footer />
    </PageWrapper>
  );
}
